import React from "react";
import { ThemeProvider } from "styled-components";
import { theme, themeChakraUi } from "./styles/theme";
import { GlobalStyle } from "./styles/globalStyles";
import { Outlet } from "react-router-dom";
import LoginContextProvider from "./context/loginContext";
import UsuallyContextProvider from "@context/usuallyContex";
import { ChakraProvider } from "@chakra-ui/react";
import OportunityContexProvider from "@context/oportunityContex";
import { datadogRum } from "@datadog/browser-rum";
import MenuSideBar from "@components/common/MenuSideBar";
import ErrorBoundary from "@components/common/ErrorBoundary";
import maintenanceServiceInstance from "@services/Maintenance/MaintanceService";
import Spinner from "@components/common/Spinner";
import Maintenance from "./pages/Maintenance";
import ChatBot from "@components/common/ChatBot";
import AppDownloadModal from "@components/common/AppDownloadModal";
import { useAppDownloadModal } from "@hook/useAppDownloadModal";

export const ThemeContext: any = React.createContext(null);

const handleConfigsByEnviroment = () => {
  if (window.location.href.includes("localhost")) return;

  if (window.location.href.includes("dev")) return;

  if (window.location.href.includes("qa")) return;

  if (window.location.href.includes("stage"))
    return {
      sessionReplaySampleRate: 0,
      sessionSampleRate: 100,
    };

  return {
    sessionReplaySampleRate: 20,
    sessionSampleRate: 25,
  };
};

const handleConfigsEnv = () => {
  if (window.location.href.includes("localhost")) return "dev";

  if (window.location.href.includes("dev")) return "dev";

  if (window.location.href.includes("qa")) return "hmg";

  if (window.location.href.includes("stage")) return "stage";

  return "prod";
};

// Componente interno para usar os hooks após os providers
const AppContent: React.FC = () => {
  const { showModal, closeModal } = useAppDownloadModal();

  // Memoize o layout principal para evitar re-renderizações
  const appLayout = React.useMemo(
    () => (
      <>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "100vw",
          }}
        >
          <GlobalStyle />
          <MenuSideBar />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
            }}
          >
            <Outlet />
          </div>
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
          }}
        >
          <ChatBot />
        </div>

        {/* Modal de download do app - apenas para mobile web */}
        <AppDownloadModal isOpen={showModal} onClose={closeModal} />
      </>
    ),
    [showModal, closeModal]
  );

  return appLayout;
};

function App() {
  const [themes, setThemes] = React.useState("");
  const themeGlobal = React.useMemo(() => ({ themes, setThemes }), [themes]);
  //Exibir Tela de Manutenção
  const [isMaintenanceMode, setIsMaintenanceMode] = React.useState(false);
  const [isCheckingMaintenance, setIsCheckingMaintenance] =
    React.useState(true);

  // Verificar o status de manutenção quando o componente montar
  React.useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const response =
          await maintenanceServiceInstance.getStatusMaintenance();

        setIsMaintenanceMode(response);
      } catch (error) {
        console.error("Erro ao verificar status de manutenção:", error);
        setIsMaintenanceMode(false);
      } finally {
        setIsCheckingMaintenance(false);
      }
    };

    checkMaintenanceStatus();
  }, []);

  // Initialize Datadog RUM (moved inside component to avoid conditional hook call)
  React.useEffect(() => {
    datadogRum.init({
      applicationId: "ad82704a-fe05-44df-b5d2-8f140fff89ff",
      clientToken: "pubed4ba6a30b7d1b2b3bd7c69997536cac",
      site: "datadoghq.com",
      service: "boravender-web",
      env: handleConfigsEnv(),
      ...handleConfigsByEnviroment,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      allowedTracingUrls: [
        (url) =>
          url.startsWith("https://apidev.direcional.com.br/corretorapi/v1"),
        (url) =>
          url.startsWith("https://apihmg.direcional.com.br/corretorapi/v1"),
        (url) =>
          url.startsWith("https://apistage.direcional.com.br/corretorapi/v1"),
        (url) => url.startsWith("https://api.direcional.com.br/corretorapi/v1"),
        (url) => url.startsWith("https://backend-dev-eks.boravender.app.br"),
        (url) => url.startsWith("https://backend-qa-eks.boravender.app.br"),
        (url) => url.startsWith("https://backend-stage-eks.boravender.app.br"),
        (url) => url.startsWith("https://backend-prod-eks.boravender.app.br"),
      ],
    });
  }, []);

  // Mostrar um indicador de carregamento enquanto verifica o status de manutenção
  if (isCheckingMaintenance) {
    return (
      <ChakraProvider theme={themeChakraUi}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Spinner />
        </div>
      </ChakraProvider>
    );
  }

  // Mostrar a tela de manutenção se o site estiver em manutenção
  if (isMaintenanceMode) {
    return (
      <ChakraProvider theme={themeChakraUi}>
        <Maintenance />
      </ChakraProvider>
    );
  }

  return (
    <ErrorBoundary>
      <LoginContextProvider>
        <UsuallyContextProvider>
          <OportunityContexProvider>
            <ThemeContext.Provider value={themeGlobal}>
              <ThemeProvider theme={theme}>
                <ChakraProvider theme={themeChakraUi}>
                  <AppContent />
                </ChakraProvider>
              </ThemeProvider>
            </ThemeContext.Provider>
          </OportunityContexProvider>
        </UsuallyContextProvider>
      </LoginContextProvider>
    </ErrorBoundary>
  );
}

export default App;
